import isEqual from 'lodash/isEqual';
import { useRef } from 'react';

import type { ExtensionViewportSize } from '@atlaskit/editor-common/types';

import { useExtensionList, FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui';
import { fg } from '@confluence/feature-gating';
import { usePageContentId } from '@confluence/page-context';

const DEFAULT_EXTENSION_VIEWPORT_SIZES = [];

// Hook to retrieve forge app viewport sizes for initial SSR render
// clone of next/packages/content-renderer/src/hooks/useExtensionViewportSizes.ts which is used for content-renderer
export const useExtensionViewportSizes = () => {
	const [contentId] = usePageContentId();
	const VIEWPORT_SIZE_KEY = `${FORGE_MODULE_XEN_MACRO}:viewportSizes:${contentId}`;

	const filteredExtensions =
		(window.__SSR_FORGE_EXTENSIONS__?.[VIEWPORT_SIZE_KEY]
			?.extensions as unknown as ExtensionViewportSize[]) ?? DEFAULT_EXTENSION_VIEWPORT_SIZES;

	// Technically here cached value can have length of 0.
	// In this case, we don't want to skip the query as we have no way to tell whether its from cache or just default value.
	const hasCachedValue = fg('cc_complexit_fe_reduce_rerender') && filteredExtensions.length > 0;

	const { extensions, loading, error } = useExtensionList({
		moduleType: FORGE_MODULE_XEN_MACRO,
		skipQuery: !fg('confluence_preload_forge_viewport_heights_editor') || hasCachedValue,
	});

	const prevExtensionViewportSizes = useRef(filteredExtensions);

	if (loading || error || !extensions) {
		return fg('cc_complexit_fe_reduce_rerender') ? filteredExtensions : [];
	}

	if (fg('cc_complexit_fe_reduce_rerender')) {
		const nextExtensionViewportSizes = extensions
			.map(({ id, properties }) =>
				properties?.viewportSize
					? { extensionId: id, viewportSize: properties.viewportSize }
					: undefined,
			)
			.filter(Boolean) as unknown as ExtensionViewportSize[];

		if (
			prevExtensionViewportSizes.current.length !== nextExtensionViewportSizes.length ||
			!isEqual(prevExtensionViewportSizes.current, nextExtensionViewportSizes)
		) {
			// @ts-ignore FIXME: Find the right type during clean up.
			prevExtensionViewportSizes.current = nextExtensionViewportSizes;

			// Write to window object to cache the viewport sizes.
			if (window.__SSR_FORGE_EXTENSIONS__) {
				window.__SSR_FORGE_EXTENSIONS__ = {
					...window.__SSR_FORGE_EXTENSIONS__,
					[VIEWPORT_SIZE_KEY]: {
						extensions: nextExtensionViewportSizes,
					},
				};
			}
		}
		return prevExtensionViewportSizes.current as unknown as ExtensionViewportSize[];
	}

	return extensions
		.map((extension) =>
			extension.properties?.viewportSize
				? { extensionId: extension.id, viewportSize: extension.properties.viewportSize }
				: {},
		)
		.filter((item) => Object.keys(item).length > 0);
};
